import React, { SyntheticEvent, useEffect, useState } from 'react';
import Modal from '../../../components/shared/Modal/Modal';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import Select from '../../../components/shared/Select/Select';
import Button from '../../../components/shared/Button/Button';
import { createExpense } from '../../../services/expenses';
import type { ExpenseCategory } from '../../../types/api';

const CATEGORY_LABEL: Record<ExpenseCategory, string> = {
  fuel: 'Fuel',
  loading_zone: 'Loading zone',
  driver_pay: 'Driver pay',
  maintenance: 'Maintenance',
  other: 'Other',
};

interface Props {
  isOpen: boolean;
  tripId: number;
  /** Human label for the fixed trip (route · vehicle), shown read-only. */
  tripLabel: string;
  onClose: () => void;
  onCreated?: () => void;
}

/**
 * Quick "add expense" modal for the manifest — the trip is fixed to the manifest's
 * trip (its vehicle), so only category + amount + description are asked.
 */
const AddExpenseModal: React.FC<Props> = ({ isOpen, tripId, tripLabel, onClose, onCreated }) => {
  const [category, setCategory] = useState<ExpenseCategory>('fuel');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCategory('fuel');
      setAmount('');
      setDescription('');
      setError(null);
    }
  }, [isOpen]);

  const canSave = parseFloat(amount) > 0 && !saving;

  const handleSave = async (e?: SyntheticEvent) => {
    e?.preventDefault();
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await createExpense({ trip: tripId, category, amount, description });
      onCreated?.();
      onClose();
    } catch {
      setError('Failed to add expense.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add expense">
      <form onSubmit={handleSave} className="space-y-4">
        <FormField label="Trip" name="trip">
          <div className="text-sm text-slate-700">{tripLabel}</div>
        </FormField>
        <FormField label="Category" name="category" required>
          <Select
            value={category}
            onChange={(v) => setCategory(v as ExpenseCategory)}
            options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))}
          />
        </FormField>
        <FormField label="Amount (KES)" name="amount" required>
          <Input id="amount" type="number" step="0.01" required value={amount}
            onChange={(e) => setAmount(e.target.value)} />
        </FormField>
        <FormField label="Description" name="description">
          <Input id="description" value={description}
            onChange={(e) => setDescription(e.target.value)} />
        </FormField>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>Record expense</Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddExpenseModal;
